import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { Sheet } from '@/components/ui/Sheet'
import { Switch } from '@/components/ui/Toggle'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { Profile } from '@/types/api'

import { PlusIcon, SaveFooter, SectionCard, TrashIcon, useIsPhone, useSectionForm } from './shared'

interface SkillRow {
  key: string
  /** Set for skills already on the profile. */
  id: string | null
  name: string
  years: string
  isPrimary: boolean
}

const skillsFrom = (profile: Profile): SkillRow[] =>
  profile.skills.map((skill) => ({
    key: skill.id,
    id: skill.id,
    name: skill.name,
    years: skill.yearsOfExperience?.toString() ?? '',
    isPrimary: skill.isPrimary,
  }))

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/** "4y", "0.5y", or nothing when the years are not known. */
const yearsLabel = (years: string) => (years.trim() === '' ? null : `${Number(years)}y`)

export function SkillsTab({ profile }: { profile: Profile }) {
  const queryClient = useQueryClient()
  const phone = useIsPhone()
  const { form: rows, setForm: setRows, dirty, markSaved, savedAt } = useSectionForm(profile, skillsFrom)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const patch = (key: string, changes: Partial<SkillRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)))
  const removeRow = (key: string) => setRows((current) => current.filter((row) => row.key !== key))

  /** Adds to the draft; a duplicate name is refused with the reason. */
  const addRow = (draft: { name: string; years: string; isPrimary: boolean }): string | null => {
    const name = draft.name.trim()
    if (!name) return 'Type a skill first.'
    if (rows.some((row) => sameName(row.name, name))) return `${name} is already listed.`
    const years = draft.years.trim()
    if (years !== '' && !(Number(years) >= 0 && Number(years) <= 60)) return 'Years must be between 0 and 60.'
    setRows((current) => [
      ...current,
      { key: `new-${Date.now()}-${current.length}`, id: null, name, years, isPrimary: draft.isPrimary },
    ])
    return null
  }

  const save = useMutation({
    mutationFn: async () => {
      const kept = rows.filter((row) => row.name.trim())
      const names = kept.map((row) => row.name.trim().toLowerCase())
      if (new Set(names).size !== names.length) {
        throw new ApiError('A skill is listed twice', 422, 'validation_error', null)
      }
      const byId = new Map(profile.skills.map((skill) => [skill.id, skill]))

      // Skills are keyed by name on the server: a renamed one is a remove plus an add.
      const toRemove: string[] = []
      const toUpsert: Array<{ name: string; yearsOfExperience?: number; isPrimary: boolean }> = []
      for (const [id, skill] of byId) {
        const row = kept.find((r) => r.id === id)
        if (!row || !sameName(row.name, skill.name)) toRemove.push(id)
      }
      for (const row of kept) {
        const before = row.id ? byId.get(row.id) : undefined
        const years = row.years.trim() === '' ? undefined : Number(row.years)
        if (years !== undefined && !(years >= 0 && years <= 60)) {
          throw new ApiError(`Years for ${row.name} must be between 0 and 60`, 422, 'validation_error', null)
        }
        const unchanged =
          before &&
          sameName(row.name, before.name) &&
          row.name === before.name &&
          (years ?? null) === before.yearsOfExperience &&
          row.isPrimary === before.isPrimary
        if (unchanged) continue
        toUpsert.push({
          name: row.name.trim(),
          ...(years !== undefined ? { yearsOfExperience: years } : {}),
          isPrimary: row.isPrimary,
        })
      }
      for (const id of toRemove) await profileApi.removeSkill(id)
      if (toUpsert.length) await profileApi.updateSkills(toUpsert)
      return { removed: toRemove.length, saved: toUpsert.length }
    },
    onSuccess: () => {
      markSaved()
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['profile-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['career-paths'] })
      toast.success('Saved')
    },
    onError: (error: ApiError) => toast.error('Could not save', error.message),
  })

  const filled = rows.filter((row) => row.name.trim()).length
  const withYears = rows.filter((row) => row.name.trim() && row.years.trim() !== '').length
  const primary = rows.filter((row) => row.isPrimary)
  const other = rows.filter((row) => !row.isPrimary)
  const editingRow = rows.find((row) => row.key === editing) ?? null

  const hint =
    filled < 5 ? (
      <>
        <span className="num">{filled}</span> of <span className="num">5</span> skills added.
      </>
    ) : withYears < 3 ? (
      <>
        <span className="num">{filled}</span> skills. Years on <span className="num">{withYears}</span>; add years to at least 3.
      </>
    ) : (
      <>
        <span className="num">{filled}</span> skills, years on <span className="num">{withYears}</span>.
      </>
    )

  return (
    <SectionCard
      id="profile-skills"
      title="Skills"
      description="Add your top skills and technologies. Frameworks, languages and tools are all important to share. Add at least five, with years where you can."
      actions={
        <span className="text-[12.5px] text-ink-muted">
          <span className="num font-medium text-ink">{filled}</span> {filled === 1 ? 'skill' : 'skills'}
        </span>
      }
      footer={
        <SaveFooter onSave={() => save.mutate()} saving={save.isPending} dirty={dirty} savedAt={savedAt} hint={hint} />
      }
    >
      {/* Add: one row on a desktop, a sheet on a phone. */}
      {phone ? (
        <Button variant="secondary" fullWidth icon={<PlusIcon />} onClick={() => setAdding(true)}>
          Add skill
        </Button>
      ) : (
        <AddSkillRow onAdd={addRow} />
      )}

      {rows.length === 0 ? (
        <p className="panel mt-4 px-4 py-3 text-[14px] text-ink-muted">
          No skills yet. Add them here, or upload a resume on the Profile tab and we will pull them out for
          you.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {primary.length > 0 && (
            <ChipGroup
              title="Primary"
              description="What you lead with. Shown first to employers."
              rows={primary}
              onEdit={setEditing}
              onRemove={removeRow}
            />
          )}
          {other.length > 0 && (
            <ChipGroup
              title={primary.length > 0 ? 'Other skills' : 'Skills'}
              description="Tap a skill to add years or mark it primary."
              rows={other}
              onEdit={setEditing}
              onRemove={removeRow}
            />
          )}
        </div>
      )}

      <Sheet open={adding} onClose={() => setAdding(false)} title="Add a skill" side="bottom">
        <AddSkillForm
          onAdd={(draft) => {
            const problem = addRow(draft)
            if (!problem) setAdding(false)
            return problem
          }}
        />
      </Sheet>

      <Sheet
        open={editingRow !== null}
        onClose={() => setEditing(null)}
        title={editingRow?.name || 'Skill'}
        description="Changes are kept here until you press Save."
        side="bottom"
        footer={
          editingRow && (
            <>
              <Button
                variant="ghost"
                className="mr-auto text-danger hover:text-danger"
                icon={<TrashIcon />}
                onClick={() => {
                  removeRow(editingRow.key)
                  setEditing(null)
                }}
              >
                Remove
              </Button>
              <Button onClick={() => setEditing(null)}>Done</Button>
            </>
          )
        }
      >
        {editingRow && (
          <SkillFields
            name={editingRow.name}
            years={editingRow.years}
            isPrimary={editingRow.isPrimary}
            onChange={(changes) => patch(editingRow.key, changes)}
          />
        )}
      </Sheet>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------
function ChipGroup({
  title,
  description,
  rows,
  onEdit,
  onRemove,
}: {
  title: string
  description: string
  rows: SkillRow[]
  onEdit: (key: string) => void
  onRemove: (key: string) => void
}) {
  return (
    <section className="panel p-4 sm:p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="label-eyebrow">{title}</h3>
        <span className="num text-[12px] text-ink-faint">{rows.length}</span>
      </div>
      <p className="mt-1 text-[12.5px] text-ink-muted">{description}</p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {rows.map((row) => (
          <li key={row.key} className="max-w-full">
            <SkillChip row={row} onEdit={() => onEdit(row.key)} onRemove={() => onRemove(row.key)} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function SkillChip({ row, onEdit, onRemove }: { row: SkillRow; onEdit: () => void; onRemove: () => void }) {
  const years = yearsLabel(row.years)
  return (
    <span
      className={cn(
        'inline-flex h-10 max-w-full items-stretch overflow-hidden rounded-pill border text-[13.5px] transition-colors',
        row.isPrimary ? 'border-brand/40 bg-brand-soft text-ink' : 'border-line bg-surface text-ink',
      )}
    >
      <button
        type="button"
        onClick={onEdit}
        className="flex min-w-0 items-center gap-2 pl-3.5 pr-2 hover:bg-ink/[0.04]"
        title={`Edit ${row.name}`}
      >
        <span className="truncate font-medium">{row.name || 'Unnamed skill'}</span>
        <span className={cn('num shrink-0 text-[12px]', years ? 'text-ink-muted' : 'text-ink-faint')}>
          {years ?? '— y'}
        </span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${row.name || 'skill'}`}
        className="grid w-9 shrink-0 place-items-center border-l border-line/70 text-ink-muted hover:bg-danger/10 hover:text-danger"
      >
        <svg className="h-3 w-3" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="m2 2 6 6M8 2 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Adding
// ---------------------------------------------------------------------------
type Draft = { name: string; years: string; isPrimary: boolean }
const EMPTY: Draft = { name: '', years: '', isPrimary: false }

/** The desktop add row: skill, years, primary, Add — all on one line. */
function AddSkillRow({ onAdd }: { onAdd: (draft: Draft) => string | null }) {
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const problem = onAdd(draft)
    setError(problem)
    if (!problem) setDraft(EMPTY)
  }

  return (
    <form onSubmit={submit} className="panel p-3" aria-label="Add a skill">
      <div className="flex items-center gap-2">
        <input
          aria-label="Skill"
          value={draft.name}
          onChange={(event) => {
            setDraft((current) => ({ ...current, name: event.target.value }))
            if (error) setError(null)
          }}
          placeholder="e.g. Kubernetes"
          className="field-box h-10 min-w-0 flex-1"
        />
        <input
          aria-label="Years of experience"
          type="number"
          inputMode="decimal"
          min={0}
          max={60}
          step={0.5}
          value={draft.years}
          onChange={(event) => setDraft((current) => ({ ...current, years: event.target.value }))}
          placeholder="Years"
          className="field-box num h-10 w-24"
        />
        <label className="flex h-10 shrink-0 items-center gap-2 rounded-field border border-line px-3 text-[13px] text-ink-soft">
          Primary
          <Switch
            size="sm"
            checked={draft.isPrimary}
            onChange={(checked) => setDraft((current) => ({ ...current, isPrimary: checked }))}
            label="Primary skill"
          />
        </label>
        <Button type="submit" size="sm" icon={<PlusIcon />}>
          Add
        </Button>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-[13px] text-danger">
          {error}
        </p>
      )}
    </form>
  )
}

/** The phone add form, inside a sheet. */
function AddSkillForm({ onAdd }: { onAdd: (draft: Draft) => string | null }) {
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        setError(onAdd(draft))
      }}
      className="space-y-4 pb-2"
    >
      <SkillFields
        {...draft}
        autoFocus
        onChange={(changes) => {
          setDraft((current) => ({ ...current, ...changes }))
          if (error) setError(null)
        }}
      />
      {error && (
        <p role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      )}
      <Button type="submit" fullWidth icon={<PlusIcon />}>
        Add skill
      </Button>
    </form>
  )
}

/** Name, years and primary: the same three fields for adding and editing. */
function SkillFields({
  name,
  years,
  isPrimary,
  onChange,
  autoFocus,
}: Draft & { onChange: (changes: Partial<Draft>) => void; autoFocus?: boolean }) {
  return (
    <div className="space-y-4">
      <TextField
        label="Skill"
        appearance="box"
        value={name}
        autoFocus={autoFocus}
        placeholder="e.g. Kubernetes"
        onChange={(event) => onChange({ name: event.target.value })}
      />
      <TextField
        label="Years of experience"
        appearance="box"
        type="number"
        inputMode="decimal"
        min={0}
        max={60}
        step={0.5}
        value={years}
        placeholder="Optional"
        className="num"
        onChange={(event) => onChange({ years: event.target.value })}
      />
      <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-field bg-surface-muted/60 px-3.5 py-2">
        <span className="text-[14px] text-ink-soft">Primary skill</span>
        <Switch checked={isPrimary} onChange={(checked) => onChange({ isPrimary: checked })} label="Primary skill" />
      </div>
    </div>
  )
}
